import { resolveLocalized } from '~/utils/resolveLocalized'

export interface UseSiteSeoInput {
  title?: string | Record<string, unknown>
  description?: string | Record<string, unknown>
  image?: string
  keywords?: string[]
  canonical?: string
  noindex?: boolean
  type?: 'website' | 'article'
}

function normalizeText(value: unknown, locale: string): string {
  if (typeof value === 'string') return value.trim()
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    const localized = resolveLocalized(record, locale, [locale, 'fa', 'en'], locale)
    if (typeof localized === 'string') return localized.trim()
    if (localized && typeof localized === 'object') {
      const data = localized as Record<string, unknown>
      return String(data.fa ?? data.en ?? Object.values(data)[0] ?? '').trim()
    }
  }
  return ''
}

function pageTitleFromPath(path: string): string {
  const map: Record<string, string> = {
    '/': 'خانه',
    '/about': 'درباره ما',
    '/services': 'خدمات',
    '/booking': 'رزرو نوبت',
    '/appointment': 'نوبت‌دهی',
    '/contact': 'تماس با ما',
    '/team': 'تیم پزشکی',
    '/blog': 'مقالات و آموزش',
    '/pricing': 'قیمت‌گذاری',
    '/virtual-visit': 'ویزیت مجازی',
    '/patient': 'پورتال بیمار',
    '/patient/login': 'ورود بیمار',
  }

  return map[path] ?? (path.replace(/^\//, '').replace(/[-_]/g, ' ') || 'صفحه')
}

function pageDescriptionFromPath(path: string): string {
  const map: Record<string, string> = {
    '/': 'رزرو آنلاین نوبت، مشاوره پزشکی و خدمات حرفه‌ای درمانی در یک سیستم مدرن و سئو-ready.',
    '/about': 'معرفی تیم، تخصص‌ها و ارزش‌های پزشکی و خدمات درمانی به‌صورت حرفه‌ای و شفاف.',
    '/services': 'مشاهده خدمات پزشکی، بررسی تخصص‌ها، هزینه‌ها و رزرو آنلاین نوبت در کوتاه‌ترین زمان.',
    '/booking': 'رزرو نوبت آنلاین، انتخاب پزشک، انتخاب تاریخ و مشاهده خدمات پزشکی در یک تجربه سریع و مطمئن.',
    '/appointment': 'رزرو ویزیت حضوری و آنلاین با سیستم نوبت‌دهی حرفه‌ای و مدیریت آسان.',
    '/contact': 'اطلاعات تماس، آدرس مطب و فرم ارتباط مستقیم برای مشاوره و رزرو خدمات پزشکی.',
    '/team': 'معرفی پزشکان و تیم متخصص با تجربه حرفه‌ای در خدمات درمانی و مشاوره پزشکی.',
    '/blog': 'مقالات آموزشی پزشکی، سلامت، مراقبت و نکات مهم بهداشتی و درمانی برای بیماران.',
    '/pricing': 'آشنایی با تعرفه‌ها، خدمات پزشکی و برنامه‌های مناسب برای رزرو نوبت و ویزیت.',
    '/virtual-visit': 'ویزیت مجازی پزشکی و مشاوره آنلاین با حفظ کیفیت خدمات درمانی و امنیت اطلاعات.',
    '/patient': 'پورتال بیمار برای مشاهده نوبت‌ها، سوابق سلامت، ویزیت‌ها و مدیریت خدمات پزشکی.',
    '/patient/login': 'ورود به پنل بیمار برای رزرو نوبت، پیگیری ویزیت و مدیریت سوابق پزشکی.',
  }

  return map[path] ?? `${pageTitleFromPath(path)} — خدمات پزشکی و رزرو آنلاین نوبت با تجربه حرفه‌ای و سریع.`
}

export function useSiteSeo(input?: MaybeRefOrGetter<UseSiteSeoInput | undefined | null>) {
  const route = useRoute()
  const requestUrl = useRequestURL()
  const tenant = useTenantSite()
  const locale = useState<string>('page-locale', () => 'fa')

  const payload = computed(() => toValue(input) ?? {})

  const siteName = computed(() => {
    const fromSite = normalizeText((tenant.value?.data as Record<string, unknown> | undefined)?.title, locale.value)
    return fromSite || (tenant.value ? 'مطب' : 'AradCore')
  })

  const pageTitle = computed(() => {
    const explicit = normalizeText(payload.value.title, locale.value)
    if (explicit) return explicit
    return pageTitleFromPath(route.path)
  })

  const pageDescription = computed(() => {
    const explicit = normalizeText(payload.value.description, locale.value)
    if (explicit) return explicit
    const siteDescription = normalizeText(
      (tenant.value?.data as Record<string, unknown> | undefined)?.siteSettings?.description,
      locale.value,
    )
    if (siteDescription) return siteDescription
    return pageDescriptionFromPath(route.path)
  })

  const image = computed(() => {
    const settings = (tenant.value?.data as Record<string, unknown> | undefined)?.siteSettings as Record<string, unknown> | undefined
    const rawImage = payload.value.image || (typeof settings?.logoUrl === 'string' ? settings.logoUrl : '')
    if (!rawImage) return undefined
    try {
      return new URL(rawImage, requestUrl.origin).href
    }
    catch {
      return undefined
    }
  })

  const canonicalUrl = computed(() => {
    const requestedCanonical = payload.value.canonical?.trim()
    if (requestedCanonical) {
      try {
        const canonical = new URL(requestedCanonical, requestUrl.origin)
        if (canonical.origin === requestUrl.origin) return canonical.href
      }
      catch {
        // Fall back to the current route when the configured canonical is invalid.
      }
    }
    const path = route.path === '/' ? '' : route.path
    return new URL(path, requestUrl.origin).href
  })

  const keywords = computed(() => {
    const explicit = Array.isArray(payload.value.keywords) ? payload.value.keywords : []
    return [...new Set(explicit.map((keyword) => keyword.trim()).filter(Boolean))]
  })

  const robots = computed(() => (payload.value.noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large,max-snippet:-1'))
  const fullTitle = computed(() => {
    if (pageTitle.value === siteName.value || pageTitle.value.includes(siteName.value)) return pageTitle.value
    return `${pageTitle.value} | ${siteName.value}`
  })

  useSeoMeta({
    title: () => fullTitle.value,
    description: () => pageDescription.value,
    ogTitle: () => fullTitle.value,
    ogDescription: () => pageDescription.value,
    ogType: () => payload.value.type ?? 'website',
    ogSiteName: () => siteName.value,
    ogImage: () => image.value,
    twitterCard: 'summary_large_image',
    twitterTitle: () => fullTitle.value,
    twitterDescription: () => pageDescription.value,
    twitterImage: () => image.value,
    keywords: () => keywords.value.length ? keywords.value.join(', ') : undefined,
    robots: () => robots.value,
    author: () => siteName.value,
  })

  useHead({
    htmlAttrs: {
      lang: locale.value,
      dir: locale.value === 'fa' ? 'rtl' : 'ltr',
    },
    link: [
      { rel: 'canonical', href: canonicalUrl.value },
    ],
    meta: [
      { name: 'theme-color', content: '#0f172a' },
      { name: 'format-detection', content: 'telephone=yes' },
      { property: 'og:url', content: canonicalUrl.value },
      { property: 'twitter:url', content: canonicalUrl.value },
    ],
  })

  return {
    pageTitle,
    pageDescription,
    siteName,
    canonicalUrl,
    image,
    robots,
  }
}
